import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import type { ReactElement } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'

import { useSession } from '../auth/useSession'
import { Button, Tag } from '../components'
import { visibleAdminNav } from '../lib/adminNav'
import {
  ADMIN_PATH,
  CHANGE_PASSWORD_PATH,
  CHANGE_PIN_PATH,
  HOME_PATH,
  LOGIN_PATH,
  OPERATIONS_PATH,
} from '../lib/routes'
import { trpc } from '../trpc'
import styles from './AppShell.module.scss'

/**
 * The authenticated shell: a sticky top bar and `<main><Outlet /></main>`.
 * Mounted once by the layout route, so it renders on every guarded screen
 * instead of a top bar living inside each page.
 *
 * Each link shows only to a role holding its section — and that is a courtesy,
 * not the protection: the API refuses every procedure of a section the role
 * lacks, and `RequireSection` sends the person home if they type the URL.
 *
 * The account controls are laid out flat and not behind a dropdown on purpose:
 * this is a wall-mounted tablet operated with a gloved thumb, and a hidden menu
 * is one more 48px target to find before the one you wanted. Changing the
 * password is a LINK to its own screen rather than a dialog opened from here —
 * the shell renders on every route, and a form living in it would be a second
 * copy of `/cuenta/contrasena` to keep in step.
 */
export const AppShell = (): ReactElement => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const queryClient = useQueryClient()
  const { user, hasSection, isReadOnly } = useSession()

  const [logoutError, setLogoutError] = useState<string | null>(null)

  const logoutMutation = trpc.auth.logout.useMutation({
    onSuccess: () => {
      // The WHOLE cache, not just `auth.me`: these are shared tablets, and the
      // next person to sign in would otherwise be served the previous one's
      // groups and running operations until each query went stale.
      queryClient.clear()
      navigate(LOGIN_PATH, { replace: true })
    },
    onError: () => {
      // The session lives in an httpOnly cookie only the server can drop, so a
      // failed logout leaves it alive: navigating to /login would bounce right
      // back through the page's "already signed in" redirect. Stay put and say
      // that the session is still open.
      setLogoutError('No se pudo cerrar la sesión. Comprueba la conexión e inténtalo de nuevo.')
    },
  })

  // The administration area spends ~27rem on its sidebar; without the extra
  // width its tables (users, roles) scroll sideways at the usual 120rem.
  const isAdminArea = pathname === ADMIN_PATH || pathname.startsWith(`${ADMIN_PATH}/`)

  const navLinkClass = ({ isActive }: { isActive: boolean }): string =>
    [styles['app-shell__nav-link'], isActive ? styles['app-shell__nav-link--active'] : '']
      .filter(Boolean).join(' ')

  // `/` and `/operaciones` are the SAME screen for whoever holds the section, and
  // every way into the app lands on `/` — the login redirect, RequireSection's
  // bounce, the catch-all. A NavLink to `/operaciones` therefore matches nothing
  // where the plant actually sits, leaving the tablet with no tab lit at all.
  const operationsActive = pathname === HOME_PATH || pathname.startsWith(OPERATIONS_PATH)

  return (
    <div className={styles['app-shell']}>
      <header className={styles['app-shell__header']}>
        <span className={styles['app-shell__brand']}>WASP</span>

        <nav className={styles['app-shell__nav']} aria-label="Navegación principal">
          {hasSection('operaciones')
            ? (
              <NavLink to={OPERATIONS_PATH} className={navLinkClass({ isActive: operationsActive })}>
                Operaciones
              </NavLink>
            )
            : null}
          {/* One pill for the whole administration area, shown with any of its
              sections: the sidebar inside offers each screen by its own. */}
          {visibleAdminNav(hasSection).length > 0
            ? <NavLink to={ADMIN_PATH} className={navLinkClass}>Administración</NavLink>
            : null}
        </nav>

        <div className={styles['app-shell__account']}>
          <span className={styles['app-shell__user']}>{user?.name ?? ''}</span>
          {user !== undefined ? <Tag tone="info" label={user.roleName ?? 'Sin rol'} /> : null}
          {isReadOnly ? <Tag tone="neutral" label="Solo lectura" /> : null}
          <NavLink to={CHANGE_PASSWORD_PATH} className={navLinkClass}>Cambiar contraseña</NavLink>
          <NavLink to={CHANGE_PIN_PATH} className={navLinkClass}>Cambiar PIN</NavLink>
          <Button
            variant="secondary"
            onClick={() => {
              setLogoutError(null)
              logoutMutation.mutate()
            }}
            disabled={logoutMutation.isPending}
            label={logoutMutation.isPending ? 'Saliendo…' : 'Salir'}
          />
        </div>

        {logoutError !== null && (
          <p role="alert" className={styles['app-shell__error']}>{logoutError}</p>
        )}
      </header>

      <main className={isAdminArea
        ? `${styles['app-shell__main']} ${styles['app-shell__main--wide']}`
        : styles['app-shell__main']}
      >
        <Outlet />
      </main>
    </div>
  )
}
